import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { Link } from 'react-router-dom';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import { reloadCurrentClient } from '$/store/client/actions';
import { getAllCloDeals, deleteClientCloDeal, addClientCloDeal } from '$/store/cloDeal/actions';
import { AgGridReact } from 'ag-grid-react';
import ForbiddenCharFilter from '$/components/global/floatingFilters/ForbiddenCharFilter';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import DeleteButton from '$/components/grid/DeleteButton';

interface IClientCloDealAccessProps {
  client?: Bixby.Models.Client;
  clos?: Bixby.Models.CloDealSlim[] | undefined;
  isSuperAdmin?: boolean;
  dispatchGetClos: () => Promise<void>;
  dispatchAddClientClo: (clientId: string, cloId: string) => Promise<void>;
  dispatchReloadCurrentClient: () => Promise<void>;
  dispatchDeleteClientClo: (clientId: string, cloId: string) => Promise<void>;
}

interface DeleteCloModel {
  active: boolean;
  cloId: string | undefined;
  cloName: string | undefined;
}

const initialDeleteCloState = {
  active: false,
  cloId: undefined,
  cloName: undefined,
};

const ClientCloDealAccess: React.FC<IClientCloDealAccessProps> = props => {
  const { client, clos, dispatchGetClos, dispatchAddClientClo, isSuperAdmin, dispatchDeleteClientClo } = props;
  const [orderedClos, setOrderedClos] = useState(clos as Bixby.Models.CloDealSlim[]);
  const [filteredClos, setFilteredClos] = useState([] as Bixby.Models.CloDealSlim[]);
  const [clo, setClo] = useState(undefined as Bixby.Models.CloDealSlim | undefined);
  const [addCloModalActive, setAddCloModalActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [deleteCloModal, setDeleteCloModal] = useState(initialDeleteCloState as DeleteCloModel);

  const clientClos = useMemo(() => (client && client.cloDeals ? client.cloDeals : undefined), [client]);
  useEffect(() => {
    const getCloDispatcher = async () => {
      await dispatchGetClos();
    };
    getCloDispatcher();
  }, [dispatchGetClos]);

  useEffect(() => {
    setOrderedClos(
      Bixby.Utils.orderBy<Bixby.Models.CloDealSlim>(clos, c => c.name).filter(clo => clo.fileName !== null)
    );
  }, [clos]);

  const handleAddClo = async () => {
    if (client && clo) {
      setIsLoading(true);
      await dispatchAddClientClo(client.id, clo.id);
    }
    setAddCloModalActive(false);
    setIsLoading(false);
    setClo(undefined);
  };

  const handleOpenConfirmAddClo = () => {
    setAddCloModalActive(true);
  };

  const handleCancelAddClo = () => {
    setAddCloModalActive(false);
  };

  const handleCloChange = (ev: React.FormEvent<HTMLInputElement>) => {
    const selected = clos?.find(c => c.id === ev.currentTarget.value);
    setClo(selected);
  };

  const handleDeleteClo = async () => {
    if (client && (deleteCloModal || ({} as DeleteCloModel)).cloId) {
      await dispatchDeleteClientClo(client.id, deleteCloModal.cloId || '');
    }

    setDeleteCloModal({ ...initialDeleteCloState });
  };

  const handleOpenConfirmDeleteClo = (row: Bixby.Models.ClientCloDeal) => {
    setDeleteCloModal({
      active: true,
      cloId: row.cloId,
      cloName: row.cloName,
    });

    return Promise.resolve();
  };

  const handleCancelDeleteClo = () => {
    setDeleteCloModal({ ...initialDeleteCloState });
  };

  const deleteButtonCellRenderer = (props: any) => {
    const handleDeleteButton = () => {
      handleOpenConfirmDeleteClo(props.data);
      return Promise.resolve();
    };
    return <DeleteButton row={props.data} deleter={handleDeleteButton} />;
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getExactMatchDataList = (evt: any) => {
    const input = evt.target.value;
    if (!input) {
      setFilteredClos(orderedClos);
      return;
    }

    const filtered: Bixby.Models.CloDealSlim[] | [] = (orderedClos ?? []).filter(
      c =>
        (c.name ?? '').toLowerCase().indexOf((input ?? '').toLowerCase()) != -1 ||
        (c.manager ?? '').toLowerCase().indexOf((input ?? '').toLowerCase()) != -1 ||
        (c.fileName ?? '').toLowerCase().indexOf((input ?? '').toLowerCase()) != -1
    );

    setFilteredClos(filtered);
  };

  const fillDataListIfEmpty = (evt: any) => {
    const input = evt.target.value;

    if (!input) {
      setFilteredClos(orderedClos);
    }
  };

  return clos && client ? (
    <>
      <section className="client_cloaccess">
        {clientClos && !Bixby.Utils.isNullOrEmpty(clientClos) ? (
          <div className="client_cloaccess_datagrid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 150,
                    resizable: true,
                    sortable: true,
                    lockPinned: true,
                  }}
                  onGridReady={onGridReady}
                  rowData={clientClos}
                  suppressDragLeaveHidesColumns={true}
                  suppressClipboardPaste={true}
                  suppressMovableColumns={true}
                  getContextMenuItems={getContextMenuItems}
                  animateRows={true}
                  headerHeight={32}
                  rowHeight={32}
                  components={{ forbiddenCharFilter: ForbiddenCharFilter }}
                  columnDefs={[
                    { minWidth: 50, cellRenderer: deleteButtonCellRenderer },
                    {
                      field: 'cloName',
                      headerName: 'CLO Name',
                      minWidth: 290,
                      filter: 'agTextColumnFilter',
                      floatingFilter: true,
                      floatingFilterComponent: 'forbiddenCharFilter',
                      cellRenderer: (params: ICellRendererParams) => (
                        <Link
                          title={params.data['cloName']}
                          className="link_underline"
                          to={`/admin/clodeal/${params.data['cloId']}`}>
                          {params.data['cloName']}
                        </Link>
                      ),
                    },
                    {
                      field: 'firstAccessDate',
                      headerName: 'Date of Access',
                      width: 150,
                      cellRenderer: (params: ICellRendererParams) => {
                        return params.value ? new Date(params.value).toLocaleDateString() : 'Access Pending';
                      },
                    },
                    {
                      field: 'isActive',
                      headerName: 'Is Active?',
                      width: 150,
                      valueFormatter: (params: any) => {
                        return params.value ? 'Yes' : 'No';
                      },
                    },
                  ]}></AgGridReact>
              </div>
            </div>
          </div>
        ) : (
          <p className="space_around">{`There are no companies associated with ${client.name}.`}</p>
        )}
        {isSuperAdmin ? (
          <div className="add_clo">
            <h3>Add a CLO</h3>
            <InputBlock title={`Associate an existing clo with ${client.name}`}>
              <input
                list="clos"
                name="clo"
                onChange={handleCloChange}
                onInput={getExactMatchDataList}
                onFocus={fillDataListIfEmpty}
                autoComplete="off"
              />
              <datalist id="clos">
                {filteredClos &&
                  filteredClos.map((result, ix) => {
                    return (
                      <option key={ix} label={result.name} value={result.id}>
                        {result.name}
                      </option>
                    );
                  })}
              </datalist>
            </InputBlock>
            <BixButton
              type="button"
              disabled={!Bixby.Utils.isDefined(clo) || !isSuperAdmin}
              onClick={handleOpenConfirmAddClo}>
              Add to Client
            </BixButton>
            <Modal title="Add to Client" active={addCloModalActive} onClose={handleCancelAddClo}>
              <p>
                Allow {client.name} to access {(clo || ({} as any)).name}?
              </p>
              <div className="button_side_wrap">
                <BixButton type="button" onClick={handleAddClo} disabled={isLoading}>
                  Add {(clo || ({} as any)).name}
                </BixButton>
                <BixButton light type="button" onClick={handleCancelAddClo}>
                  Cancel
                </BixButton>
              </div>
            </Modal>
          </div>
        ) : null}
        <Modal
          title="Remove CLO Access"
          active={(deleteCloModal || ({} as DeleteCloModel)).active || false}
          onClose={handleCancelDeleteClo}>
          <p>Remove access to CLO {(deleteCloModal || ({} as DeleteCloModel)).cloName}?</p>
          <div className="button_side_wrap">
            <BixButton type="button" onClick={handleDeleteClo}>
              Remove {(deleteCloModal || ({} as DeleteCloModel)).cloName}
            </BixButton>
            <BixButton light type="button" onClick={handleCancelDeleteClo}>
              Cancel
            </BixButton>
          </div>
        </Modal>
      </section>
    </>
  ) : (
    <></>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetClos: () => dispatch(getAllCloDeals()),
  dispatchAddClientClo: (clientId: string, cloId: string) =>
    dispatch(addClientCloDeal(clientId, cloId, () => dispatch(reloadCurrentClient()))),
  dispatchDeleteClientClo: (clientId: string, cloId: string) =>
    dispatch(deleteClientCloDeal(clientId, cloId, () => dispatch(reloadCurrentClient()))),
  dispatchReloadCurrentClient: () => dispatch(reloadCurrentClient()),
});
const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isSuperAdmin,
    client: state.clientState.currentClient,
    clos: state.cloDealState.cloDeals,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientCloDealAccess);
